import React, { useEffect, useState, Fragment } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import htmlParser from 'html-react-parser';
import LoadingOverlay from "react-loading-overlay-ts";
import DNALoader from "../../components/Loaders/DnaLoader";
import { uploadFile } from "../../utils/fileUtils";
import "../../assets/custom css/loadoverlay.scss";

const API_URL = process.env.REACT_APP_APIURL || (process.env.REACT_APP_BASEURL ? process.env.REACT_APP_BASEURL.replace('/api/settings/', '/api/') : "");
const MAX_ATTACHMENTS = 10;
const ACCEPTED_ATTACHMENT_TYPES = ".jpeg,.jpg,.png,.gif,.webp,.pdf,.doc,.docx,image/jpeg,image/png,image/gif,image/webp,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document";

const PublicContentPage = ({ type, app }) => {
    const [content, setContent] = useState("");
    const [loading, setLoading] = useState(true);
    const [title, setTitle] = useState("");
    const [lastUpdated, setLastUpdated] = useState("");
    const [contactForm, setContactForm] = useState({
        name: "",
        email: "",
        subject: "",
        message: ""
    });
    const [contactAttachments, setContactAttachments] = useState([]);
    const [contactSubmitting, setContactSubmitting] = useState(false);
    const [contactSuccess, setContactSuccess] = useState("");
    const [contactError, setContactError] = useState("");

    useEffect(() => {
        fetchContent();
        updateTitle();
        // Set a default last updated date or fetch it if available
        setLastUpdated("January 26, 2026");
    }, [type, app]);

    const updateTitle = () => {
        if (type === 'privacy') setTitle("Privacy Policy");
        else if (type === 'support') setTitle("Support");
    };

    const fetchContent = async () => {
        setLoading(true);
        try {
            const baseUrl = API_URL.endsWith('/') ? API_URL : `${API_URL}/`;
            const response = await axios.get(`${baseUrl}public-configurations`);
            if (response.data.status) {
                const dataKey = type === 'privacy' ? `privacy_policy_${app}` : `support_info_${app}`;
                setContent(response.data.data[dataKey] || "<p>No content available.</p>");
            } else {
                setContent("<p>No content available.</p>");
            }
        } catch (error) {
            console.error("Error fetching public content:", error);
            setContent("<p>Error loading content. Please try again later.</p>");
        } finally {
            setLoading(false);
        }
    };

    const handleContactChange = (e) => {
        const { name, value } = e.target;
        setContactForm((prev) => ({
            ...prev,
            [name]: value
        }));
    };

    const getContactType = () => {
        if (!type || type !== "support") return "";
        if (app === "doctor") return "doctor";
        if (app === "pharmacy") return "pharmacy";
        if (app === "patient") return "patient";
        return "web";
    };

    const handleAttachmentSelect = (e) => {
        const selected = Array.from(e.target.files || []);
        e.target.value = "";
        if (!selected.length) return;

        setContactAttachments((prev) => {
            const remaining = MAX_ATTACHMENTS - prev.length;
            if (remaining <= 0) {
                setContactError(`You can attach up to ${MAX_ATTACHMENTS} files.`);
                return prev;
            }
            const nextFiles = selected.slice(0, remaining).map((file) => ({
                id: `${file.name}-${file.size}-${file.lastModified}-${Math.random().toString(36).slice(2)}`,
                file,
            }));
            if (selected.length > remaining) {
                setContactError(`Only ${MAX_ATTACHMENTS} attachments are allowed. Extra files were skipped.`);
            } else {
                setContactError("");
            }
            return [...prev, ...nextFiles];
        });
    };

    const removeAttachment = (id) => {
        setContactAttachments((prev) => prev.filter((item) => item.id !== id));
    };

    const handleContactSubmit = async (e) => {
        e.preventDefault();
        setContactSuccess("");
        setContactError("");

        // basic front-end validation
        if (!contactForm.name || !contactForm.email || !contactForm.subject || !contactForm.message) {
            setContactError("Please fill in all required fields.");
            return;
        }

        try {
            setContactSubmitting(true);
            const baseUrl = API_URL.endsWith('/') ? API_URL : `${API_URL}/`;

            let uploadedAttachments = [];
            if (contactAttachments.length > 0) {
                uploadedAttachments = await Promise.all(
                    contactAttachments.map(async ({ file }) => {
                        const path = await uploadFile(file, "file");
                        return {
                            path,
                            filename: path.split("/").pop() || file.name,
                            originalname: file.name,
                            mimetype: file.type || "",
                            size: file.size,
                        };
                    })
                );
            }

            const payload = {
                postData: {
                    ...contactForm,
                    type: getContactType(),
                    attachments: uploadedAttachments,
                }
            };
            const response = await axios.post(`${baseUrl}ContactUsModule/AddContactUs`, payload);

            if (response.data && response.data.status) {
                setContactSuccess("Your message has been sent successfully. Our support team will contact you soon.");
                setContactForm({
                    name: "",
                    email: "",
                    subject: "",
                    message: ""
                });
                setContactAttachments([]);
            } else if (response.data && response.data.errors && Array.isArray(response.data.errors)) {
                // show first error from backend validation
                setContactError(response.data.errors[0]?.message || "Unable to send your message. Please try again.");
            } else {
                setContactError("Unable to send your message. Please try again.");
            }
        } catch (error) {
            console.error("Error submitting contact form:", error);
            setContactError(error?.message || "Server error. Please try again later.");
        } finally {
            setContactSubmitting(false);
        }
    };

    return (
        <div className="app-main__outer">
            <LoadingOverlay active={loading} spinner={<DNALoader />}>
                <div className="container" style={{ maxWidth: '1000px', margin: '0 auto', padding: '0 20px' }}>
                    {/* Header Section */}
                    <div className="header-section" style={{ marginBottom: '60px', padding: '40px 0' }}>
                        <h1 style={{
                            fontSize: '48px',
                            fontWeight: '700',
                            margin: '0 0 10px 0',
                            color: '#1a1a1a'
                        }}>{title}</h1>
                        <p style={{
                            color: '#888',
                            fontSize: '16px',
                            margin: 0
                        }}>Last updated: {lastUpdated}</p>
                    </div>

                    {/* Content Section */}
                    <div className="content-section" style={{
                        lineHeight: '1.6',
                        fontSize: '18px',
                        color: '#444'
                    }}>
                        <div className="rendered-content">
                            {htmlParser(content)}
                        </div>

                        {type === "support" && (
                            <div
                                className="contact-form-wrapper"
                                style={{
                                    marginTop: "50px",
                                    padding: "30px",
                                    borderRadius: "16px",
                                    border: "1px solid #e5e7eb",
                                    background: "#f9fafb"
                                }}
                            >
                                <h2 style={{ fontSize: "28px", marginBottom: "10px", color: "#1a1a1a" }}>
                                    Contact Support
                                </h2>
                                <p style={{ marginBottom: "20px", color: "#6b7280", fontSize: "16px" }}>
                                    Have a question or need help with the SARA {app === "doctor" ? "Doctor" : app === "pharmacy" ? "Pharmacy" : "Patient"} app?
                                    Send us a message using the form below and our support team will get back to you.
                                </p>

                                {contactSuccess && (
                                    <div
                                        style={{
                                            marginBottom: "16px",
                                            padding: "12px 16px",
                                            borderRadius: "8px",
                                            background: "#ecfdf3",
                                            color: "#166534",
                                            border: "1px solid #bbf7d0",
                                            fontSize: "14px"
                                        }}
                                    >
                                        {contactSuccess}
                                    </div>
                                )}

                                {contactError && (
                                    <div
                                        style={{
                                            marginBottom: "16px",
                                            padding: "12px 16px",
                                            borderRadius: "8px",
                                            background: "#fef2f2",
                                            color: "#b91c1c",
                                            border: "1px solid #fecaca",
                                            fontSize: "14px"
                                        }}
                                    >
                                        {contactError}
                                    </div>
                                )}

                                <form onSubmit={handleContactSubmit}>
                                    <div
                                        style={{
                                            display: "grid",
                                            gridTemplateColumns: "1fr 1fr",
                                            gap: "16px",
                                            marginBottom: "16px"
                                        }}
                                    >
                                        <div>
                                            <label
                                                htmlFor="name"
                                                style={{ display: "block", marginBottom: "6px", fontSize: "14px", fontWeight: 500 }}
                                            >
                                                Name<span style={{ color: "#dc2626" }}> *</span>
                                            </label>
                                            <input
                                                id="name"
                                                name="name"
                                                type="text"
                                                value={contactForm.name}
                                                onChange={handleContactChange}
                                                style={{
                                                    width: "100%",
                                                    padding: "10px 12px",
                                                    borderRadius: "8px",
                                                    border: "1px solid #d1d5db",
                                                    fontSize: "14px"
                                                }}
                                            />
                                        </div>
                                        <div>
                                            <label
                                                htmlFor="email"
                                                style={{ display: "block", marginBottom: "6px", fontSize: "14px", fontWeight: 500 }}
                                            >
                                                Email<span style={{ color: "#dc2626" }}> *</span>
                                            </label>
                                            <input
                                                id="email"
                                                name="email"
                                                type="email"
                                                value={contactForm.email}
                                                onChange={handleContactChange}
                                                style={{
                                                    width: "100%",
                                                    padding: "10px 12px",
                                                    borderRadius: "8px",
                                                    border: "1px solid #d1d5db",
                                                    fontSize: "14px"
                                                }}
                                            />
                                        </div>
                                    </div>

                                    <div style={{ marginBottom: "16px" }}>
                                        <label
                                            htmlFor="subject"
                                            style={{ display: "block", marginBottom: "6px", fontSize: "14px", fontWeight: 500 }}
                                        >
                                            Subject<span style={{ color: "#dc2626" }}> *</span>
                                        </label>
                                        <input
                                            id="subject"
                                            name="subject"
                                            type="text"
                                            value={contactForm.subject}
                                            onChange={handleContactChange}
                                            style={{
                                                width: "100%",
                                                padding: "10px 12px",
                                                borderRadius: "8px",
                                                border: "1px solid #d1d5db",
                                                fontSize: "14px"
                                            }}
                                        />
                                    </div>

                                    <div style={{ marginBottom: "20px" }}>
                                        <label
                                            htmlFor="message"
                                            style={{ display: "block", marginBottom: "6px", fontSize: "14px", fontWeight: 500 }}
                                        >
                                            Message<span style={{ color: "#dc2626" }}> *</span>
                                        </label>
                                        <textarea
                                            id="message"
                                            name="message"
                                            rows="4"
                                            value={contactForm.message}
                                            onChange={handleContactChange}
                                            style={{
                                                width: "100%",
                                                padding: "10px 12px",
                                                borderRadius: "8px",
                                                border: "1px solid #d1d5db",
                                                fontSize: "14px",
                                                resize: "vertical"
                                            }}
                                        />
                                    </div>

                                    <div style={{ marginBottom: "20px" }}>
                                        <label
                                            htmlFor="attachments"
                                            style={{ display: "block", marginBottom: "6px", fontSize: "14px", fontWeight: 500 }}
                                        >
                                            Attachments
                                        </label>
                                        <p style={{ margin: "0 0 8px 0", color: "#6b7280", fontSize: "13px" }}>
                                            Optional. You can attach images, PDFs, or documents (max {MAX_ATTACHMENTS} files).
                                        </p>
                                        <input
                                            id="attachments"
                                            name="attachments"
                                            type="file"
                                            multiple
                                            accept={ACCEPTED_ATTACHMENT_TYPES}
                                            onChange={handleAttachmentSelect}
                                            disabled={contactSubmitting || contactAttachments.length >= MAX_ATTACHMENTS}
                                            style={{
                                                width: "100%",
                                                padding: "10px 12px",
                                                borderRadius: "8px",
                                                border: "1px solid #d1d5db",
                                                fontSize: "14px",
                                                background: "#fff"
                                            }}
                                        />
                                        {contactAttachments.length > 0 && (
                                            <ul style={{ listStyle: "none", padding: 0, margin: "12px 0 0 0" }}>
                                                {contactAttachments.map((item) => (
                                                    <li
                                                        key={item.id}
                                                        style={{
                                                            display: "flex",
                                                            alignItems: "center",
                                                            justifyContent: "space-between",
                                                            gap: "12px",
                                                            padding: "8px 10px",
                                                            marginBottom: "6px",
                                                            borderRadius: "8px",
                                                            border: "1px solid #e5e7eb",
                                                            background: "#fff",
                                                            fontSize: "13px"
                                                        }}
                                                    >
                                                        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                                            {item.file.name}
                                                        </span>
                                                        <button
                                                            type="button"
                                                            onClick={() => removeAttachment(item.id)}
                                                            disabled={contactSubmitting}
                                                            style={{
                                                                border: "none",
                                                                background: "transparent",
                                                                color: "#b91c1c",
                                                                cursor: "pointer",
                                                                fontWeight: 600,
                                                                flexShrink: 0
                                                            }}
                                                        >
                                                            Remove
                                                        </button>
                                                    </li>
                                                ))}
                                            </ul>
                                        )}
                                    </div>

                                    <button
                                        type="submit"
                                        disabled={contactSubmitting}
                                        style={{
                                            display: "inline-flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            padding: "10px 20px",
                                            borderRadius: "9999px",
                                            border: "none",
                                            background: contactSubmitting ? "#9ca3af" : "#f5a623",
                                            color: "#fff",
                                            fontWeight: 600,
                                            fontSize: "15px",
                                            cursor: contactSubmitting ? "not-allowed" : "pointer"
                                        }}
                                    >
                                        {contactSubmitting ? "Sending..." : "Submit Message"}
                                    </button>
                                </form>
                            </div>
                        )}
                    </div>
                </div>
            </LoadingOverlay>
            <style>
                {`
                    .rendered-content h2 {
                        font-size: 28px;
                        font-weight: 700;
                        margin: 40px 0 20px 0;
                        color: #1a1a1a;
                    }
                    .rendered-content h3 {
                        font-size: 22px;
                        font-weight: 600;
                        margin: 30px 0 15px 0;
                        color: #1a1a1a;
                    }
                    .rendered-content p {
                        margin-bottom: 20px;
                    }
                    .rendered-content a {
                        color: #f5a623;
                        text-decoration: none;
                        font-weight: 500;
                    }
                    .rendered-content a:hover {
                        text-decoration: underline;
                    }
                    .rendered-content ul, .rendered-content ol {
                        margin-bottom: 20px;
                        padding-left: 20px;
                    }
                    .rendered-content li {
                        margin-bottom: 10px;
                    }
                `}
            </style>
        </div>
    );
};

export default PublicContentPage;
